import { formatDate } from "@/lib/utils";
import React, { useState, useEffect, useMemo } from "react";
import {
  PlusCircle,
  FileText,
  Download,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  Printer,
  Package,
  RotateCcw,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { Badge } from "@/components/ui/badge";
import { ReactSelect } from "@/components/ui/react-select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import { useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { all_routes } from "../../Router/all_routes";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PageContainer } from "@/components/layout/PageContainer";

const GRNList = () => {
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Filter states
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");

  const currentStoreId = useSelector((state) => state.current_store?.id || null);
  const [selectedStoreId, setSelectedStoreId] = useState(currentStoreId || "");

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedGrnId, setSelectedGrnId] = useState(null);
  const [selectedGrn, setSelectedGrn] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [deleteConfirmationId, setDeleteConfirmationId] = useState(null);
  const [statusConfirmation, setStatusConfirmation] = useState(null);

  // Pagination state (default 15 per API documentation)
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 15,
  });

  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";

  // Keep store in sync if Redux loads store after initial render
  useEffect(() => {
    if (currentStoreId && !selectedStoreId) {
      setSelectedStoreId(currentStoreId);
    }
  }, [currentStoreId, selectedStoreId]);

  // Debounce search input (<= 255 chars)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, 350);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Query Available Payment Methods for filter
  const { data: paymentMethods = [], isLoading: paymentMethodsLoading } = useQuery({
    queryKey: [
      "payment-methods-available",
      "purchases",
      selectedStoreId || currentStoreId,
    ],
    queryFn: async () => {
      try {
        const res = await apiClient.get(API_ENDPOINTS.PAYMENT_METHODS.AVAILABLE, {
          params: {
            scope: "purchases",
            store_id: selectedStoreId || currentStoreId || undefined,
          },
        });
        const data = res.data?.data ?? res.data ?? [];
        return Array.isArray(data) ? data : [];
      } catch {
        try {
          const listRes = await apiClient.get(API_ENDPOINTS.PAYMENT_METHODS.LIST);
          const listData = listRes.data?.data ?? listRes.data ?? [];
          return Array.isArray(listData) ? listData : [];
        } catch {
          return [];
        }
      }
    },
  });

  // Query Stores for filter
  const { data: stores = [] } = useQuery({
    queryKey: ["stores-list"],
    queryFn: async () => {
      try {
        const res = await apiClient.get(API_ENDPOINTS.STORES.STORES, {
          params: { perPage: 100, per_page: 100 },
        });
        return res.data?.data || res.data || [];
      } catch {
        return [];
      }
    },
  });

  // Main GRN Query with all supported parameters
  const { data: grnData, isLoading: grnsLoading } = useQuery({
    queryKey: [
      "grns",
      pagination.pageIndex + 1,
      pagination.pageSize,
      debouncedSearchText,
      selectedStatus,
      selectedPaymentMethod,
      selectedStoreId,
    ],
    queryFn: async () => {
      const params = {
        page: pagination.pageIndex + 1,
        perPage: pagination.pageSize,
        per_page: pagination.pageSize,
        with: [
          "purchaseOrder",
          "store",
          "createdBy",
          "receivedBy",
          "items",
          "items.product",
          "items.batch",
          "paymentMethod",
          "items.batch.serials",
        ],
      };

      if (debouncedSearchText && debouncedSearchText.trim()) {
        params.search = debouncedSearchText.trim().slice(0, 255);
      }

      if (selectedStatus) {
        params.status = selectedStatus;
      }

      if (selectedPaymentMethod) {
        params.payment_method = selectedPaymentMethod;
      }

      if (selectedStoreId) {
        params.store_id = selectedStoreId;
      }

      const res = await apiClient.get(API_ENDPOINTS.PURCHASES.GRN, {
        params,
      });
      return res.data;
    },
    keepPreviousData: true,
  });

  const grns = grnData?.data || [];
  const meta = grnData?.meta || {};
  const pageCount = meta.last_page || 1;

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.USERS.EMPLOYEES, {
        params: { perPage: 100, per_page: 100 },
      });
      return res.data.data;
    },
    enabled: hasPermission("employee read"),
  });

  const filteredData = grns;

  const handleResetFilters = () => {
    setSearchText("");
    setDebouncedSearchText("");
    setSelectedStatus("");
    setSelectedPaymentMethod("");
    setSelectedStoreId(currentStoreId || "");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await apiClient.delete(API_ENDPOINTS.PURCHASES.GRN_DETAILS(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grns"] });
      toast.success("GRN Deleted Successfully");
      setDeleteConfirmationId(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete GRN");
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }) => {
      const res = await apiClient.patch(
        API_ENDPOINTS.PURCHASES.GRN_STATUS(id),
        { status },
      );
      return res.data;
    },
    onSuccess: (data, variables) => {
      toast.success(
        `GRN status updated to ${variables.status}`,
      );
      queryClient.invalidateQueries({ queryKey: ["grns"] });
      if (selectedGrnId) {
        queryClient.invalidateQueries({
          queryKey: ["grn-single-view", selectedGrnId],
        });
      }
      setSelectedGrn((prev) =>
        prev && prev.id === variables.id
          ? { ...prev, status: variables.status }
          : prev,
      );
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to update GRN status",
      );
    },
  });

  // Single GRN query for view modal with updated single view parameters
  const { data: singleGrnData, isLoading: singleGrnLoading } = useQuery({
    queryKey: ["grn-single-view", selectedGrnId],
    queryFn: async () => {
      const res = await apiClient.get(
        API_ENDPOINTS.PURCHASES.GRN_DETAILS(selectedGrnId),
        {
          params: {
            with: [
              "purchaseOrder",
              "store",
              "createdBy",
              "receivedBy",
              "items",
              "items.product",
              "items.batch",
              "paymentMethod",
              "items.batch.serials",
            ],
          },
        },
      );
      return res.data?.data || res.data;
    },
    enabled: !!selectedGrnId && isModalVisible,
  });

  const modalGrn = singleGrnData || selectedGrn;

  const fetchGrnDetails = (grnOrId) => {
    const id =
      typeof grnOrId === "object" && grnOrId !== null ? grnOrId.id : grnOrId;
    const initialData =
      typeof grnOrId === "object" && grnOrId !== null
        ? grnOrId
        : grns.find((grn) => grn.id === grnOrId);
    setSelectedGrnId(id);
    setSelectedGrn(initialData || null);
    setIsModalVisible(true);
  };

  const handleCloseModal = (open) => {
    setIsModalVisible(open);
    if (!open) {
      setSelectedGrnId(null);
      setSelectedGrn(null);
    }
  };

  const handleGeneratePdf = async (id) => {
    toast.info("Generating GRN PDF...");

    try {
      const response = await apiClient.get(API_ENDPOINTS.REPORTS.GRN_REPORT(id), {
        responseType: "blob",
      });

      if (response.status === 200) {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        setPdfBlobUrl(url);
        setShowPdfModal(true);
        toast.success("GRN PDF generated successfully!");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to generate GRN PDF",
      );
    }
  };

  const handleDownloadPDF = () => {
    if (pdfBlobUrl) {
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = `grn-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("PDF downloaded successfully!");
    }
  };

  const handlePrintPDF = () => {
    if (pdfBlobUrl) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfBlobUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        // Remove the iframe after the print dialog is closed
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
      toast.info("Opening print dialog...");
    }
  };

  const handleClosePdfModal = () => {
    setShowPdfModal(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  const getEmployeeName = (id) => {
    if (!id) return "-";
    const employee = employees.find((emp) => emp.id === id);
    return employee ? employee.full_name || employee.name : "-";
  };

  const columns = useMemo(
    () => [
      {
        accessorFn: (row) =>
          row.purchase_order?.tracking_number ||
          row.purchaseOrder?.tracking_number ||
          "-",
        header: "PO Number",
        cell: ({ getValue }) => (
          <span className="font-medium text-primary">{getValue()}</span>
        ),
      },
      {
        accessorFn: (row) => row.reference || "-",
        header: "Reference",
      },
      {
        accessorFn: (row) =>
          row.purchase_order?.supplier?.name ||
          row.purchaseOrder?.supplier?.name ||
          "-",
        header: "Supplier",
      },
      {
        accessorFn: (row) => row.store?.name || "-",
        header: "Store",
      },
      {
        accessorKey: "received_date",
        header: "Received Date",
        cell: ({ getValue }) => {
          const val = getValue();
          return val ? formatDate(val) : "-";
        },
      },
      {
        accessorFn: (row) =>
          row.receivedBy?.full_name ||
          row.receivedBy?.name ||
          row.received_by?.full_name ||
          row.received_by?.name ||
          getEmployeeName(row.received_by) ||
          "-",
        header: "Received By",
      },
      {
        accessorFn: (row) =>
          row.paymentMethod?.name ||
          row.payment_method?.name ||
          (typeof row.payment_method === "string" ? row.payment_method : "-"),
        header: "Payment Method",
        cell: ({ getValue }) => {
          const val = getValue();
          return val !== "-" ? (
            <Badge variant="outline" className="font-normal capitalize text-xs">
              {val}
            </Badge>
          ) : (
            "-"
          );
        },
      },
      {
        accessorKey: "total_amount",
        header: "Total",
        cell: ({ getValue }) =>
          `${currencySymbol}${parseFloat(getValue() || 0).toFixed(2)}`,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => {
          const status = row.original.status;
          const isCompleted = status?.toLowerCase() === "completed";
          const variants = {
            completed: "success",
            cancelled: "destructive",
            pending: "warning",
          };
          const currentVariant = variants[status?.toLowerCase()] || "default";
          const isPending =
            updateStatusMutation.isPending &&
            updateStatusMutation.variables?.id === row.original.id;

          // Completed GRNs cannot have their status changed
          if (isCompleted || !hasPermission("goods received note update_status")) {
            return (
              <Badge
                variant={currentVariant}
                className="capitalize font-medium"
              >
                {status?.replace(/_/g, " ")}
              </Badge>
            );
          }

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="cursor-pointer focus:outline-none"
                  disabled={updateStatusMutation.isPending}
                  title="Click to update status"
                >
                  <Badge
                    variant={currentVariant}
                    className="capitalize font-medium hover:opacity-80 transition-opacity gap-1"
                  >
                    {isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    ) : null}
                    {status?.replace(/_/g, " ")}
                  </Badge>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                  Change Status
                </DropdownMenuLabel>
                {status?.toLowerCase() !== "pending" && (
                  <DropdownMenuItem
                    onClick={() =>
                      setStatusConfirmation({
                        id: row.original.id,
                        status: "pending",
                        reference: row.original.reference || row.original.purchase_order?.tracking_number || "",
                      })
                    }
                  >
                    <Clock className="mr-2 h-4 w-4 text-amber-500" />
                    Pending
                  </DropdownMenuItem>
                )}
                {status?.toLowerCase() !== "completed" && (
                  <DropdownMenuItem
                    onClick={() =>
                      setStatusConfirmation({
                        id: row.original.id,
                        status: "completed",
                        reference: row.original.reference || row.original.purchase_order?.tracking_number || "",
                      })
                    }
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4 text-emerald-500" />
                    Completed
                  </DropdownMenuItem>
                )}
                {status?.toLowerCase() !== "cancelled" && (
                  <DropdownMenuItem
                    onClick={() =>
                      setStatusConfirmation({
                        id: row.original.id,
                        status: "cancelled",
                        reference: row.original.reference || row.original.purchase_order?.tracking_number || "",
                      })
                    }
                  >
                    <XCircle className="mr-2 h-4 w-4 text-rose-500" />
                    Cancelled
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const grn = row.original;
          const isCompleted = grn.status?.toLowerCase() === "completed";
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                {hasPermission("goods received note read") && (
                  <DropdownMenuItem
                    onClick={() => fetchGrnDetails(grn)}
                    onSelect={() => fetchGrnDetails(grn)}
                  >
                    <Eye className="mr-2 h-4 w-4" /> View Details
                  </DropdownMenuItem>
                )}
                {/* Completed GRNs cannot be edited */}
                {hasPermission("goods received note update") && !isCompleted && (
                  <DropdownMenuItem
                    onClick={() =>
                      navigate(all_routes.editGrn.replace(":id", grn.id))
                    }
                  >
                    <Edit className="mr-2 h-4 w-4" /> Edit
                  </DropdownMenuItem>
                )}
                {hasPermission("report goods received note view") && (
                  <DropdownMenuItem onClick={() => handleGeneratePdf(grn.id)}>
                    <FileText className="mr-2 h-4 w-4" /> Download PDF
                  </DropdownMenuItem>
                )}
                {/* Completed GRNs cannot be status changed */}
                {hasPermission("goods received note update_status") && !isCompleted && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground font-normal py-1">
                      Change Status
                    </DropdownMenuLabel>
                    {grn.status?.toLowerCase() !== "pending" && (
                      <DropdownMenuItem
                        onClick={() =>
                          setStatusConfirmation({
                            id: grn.id,
                            status: "pending",
                            reference: grn.reference || grn.purchase_order?.tracking_number || "",
                          })
                        }
                        disabled={updateStatusMutation.isPending}
                      >
                        <Clock className="mr-2 h-4 w-4 text-amber-500" />
                        Set as Pending
                      </DropdownMenuItem>
                    )}
                    {grn.status?.toLowerCase() !== "completed" && (
                      <DropdownMenuItem
                        onClick={() =>
                          setStatusConfirmation({
                            id: grn.id,
                            status: "completed",
                            reference: grn.reference || grn.purchase_order?.tracking_number || "",
                          })
                        }
                        disabled={updateStatusMutation.isPending}
                      >
                        <CheckCircle2 className="mr-2 h-4 w-4 text-emerald-500" />
                        Set as Completed
                      </DropdownMenuItem>
                    )}
                    {grn.status?.toLowerCase() !== "cancelled" && (
                      <DropdownMenuItem
                        onClick={() =>
                          setStatusConfirmation({
                            id: grn.id,
                            status: "cancelled",
                            reference: grn.reference || grn.purchase_order?.tracking_number || "",
                          })
                        }
                        disabled={updateStatusMutation.isPending}
                      >
                        <XCircle className="mr-2 h-4 w-4 text-rose-500" />
                        Set as Cancelled
                      </DropdownMenuItem>
                    )}
                  </>
                )}
                <DropdownMenuSeparator />
                {hasPermission("goods received note delete") && (
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600"
                    onClick={() => setDeleteConfirmationId(grn.id)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currencySymbol, employees, navigate, permissions, grns, updateStatusMutation, setStatusConfirmation],
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Goods Received Notes"
          subtitle="Manage your GRNs"
        />
        <PageHeaderActions>
          {hasPermission("goods received note create") && (
            <Button onClick={() => navigate(all_routes.addGrn)}>
              <PlusCircle className="mr-2 h-4 w-4" />
              Add New GRN
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 w-full items-center">
          <FilterSearch
            placeholder="Search PO, ref, supplier..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full"
          />

          <ReactSelect
            options={[
              { value: "pending", label: "Pending" },
              { value: "completed", label: "Completed" },
              { value: "cancelled", label: "Cancelled" },
            ]}
            value={
              selectedStatus
                ? [
                  { value: "pending", label: "Pending" },
                  { value: "completed", label: "Completed" },
                  { value: "cancelled", label: "Cancelled" },
                ].find((o) => o.value === selectedStatus)
                : null
            }
            onChange={(opt) => {
              setSelectedStatus(opt ? opt.value : "");
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            placeholder="All Statuses"
            isClearable
          />

          <ReactSelect
            options={paymentMethods.map((pm) => ({
              value: pm.slug || pm.id || pm.name,
              label: pm.name,
            }))}
            value={
              selectedPaymentMethod
                ? paymentMethods
                  .map((pm) => ({
                    value: pm.slug || pm.id || pm.name,
                    label: pm.name,
                  }))
                  .find((o) => o.value === selectedPaymentMethod)
                : null
            }
            onChange={(opt) => {
              setSelectedPaymentMethod(opt ? opt.value : "");
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            placeholder="All Payment Methods"
            isLoading={paymentMethodsLoading}
            isClearable
          />

          <ReactSelect
            options={stores.map((s) => ({
              value: String(s.id),
              label: s.name,
            }))}
            value={
              selectedStoreId
                ? stores
                  .map((s) => ({ value: String(s.id), label: s.name }))
                  .find((o) => o.value === String(selectedStoreId))
                : null
            }
            onChange={(opt) => {
              setSelectedStoreId(opt ? opt.value : "");
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            placeholder="All Stores"
            isClearable
          />

          <Button
            variant="outline"
            onClick={handleResetFilters}
            className="gap-2 w-full h-9"
          >
            <RotateCcw className="h-4 w-4" /> Reset Filters
          </Button>
        </div>
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={filteredData}
          enableRowSelection={true}
          pageCount={pageCount}
          pagination={pagination}
          onPaginationChange={setPagination}
          isLoading={grnsLoading}
          onRowClick={(row) => fetchGrnDetails(row)}
          footerInfo={
            <div className="flex items-center gap-4">
              <span className="font-medium text-foreground">
                Total GRNs: {meta.total ?? filteredData.length ?? 0}
              </span>
            </div>
          }
        />
      </TableCard>

      {/* Delete Confirmation Alert */}
      <AlertDialog
        open={!!deleteConfirmationId}
        onOpenChange={() => setDeleteConfirmationId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              GRN.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate(deleteConfirmationId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Status Change Confirmation Alert */}
      <AlertDialog
        open={!!statusConfirmation}
        onOpenChange={(open) => !open && setStatusConfirmation(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirm Status Change
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-foreground/80 mt-2">
                <p>
                  Are you sure you want to change the status of this Goods Received Note to{" "}
                  <span className="font-semibold capitalize text-foreground">
                    {statusConfirmation?.status}
                  </span>
                  {statusConfirmation?.reference ? ` (${statusConfirmation.reference})` : ""}?
                </p>
                <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-900/50 p-3 text-xs text-amber-800 dark:text-amber-200 space-y-1">
                  <p className="font-semibold flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
                    Action is Non-Reversible
                  </p>
                  <p>
                    {statusConfirmation?.status === "cancelled" ? (
                      <>
                        Once marked as <strong>Cancelled</strong>, the GRN cannot be edited, nor can its status be changed or reverted back to <strong>Pending or Completed</strong>.
                      </>
                    ) : statusConfirmation?.status === "completed" ? (
                      <>
                        Once marked as <strong>Completed</strong>, the GRN cannot be edited, nor can its status be changed or reverted back to <strong>Pending or Cancelled</strong>.
                      </>
                    ) : (
                      <>
                        Are you sure you want to change the status to <strong>Pending</strong>?
                      </>
                    )}
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateStatusMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={updateStatusMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (statusConfirmation) {
                  updateStatusMutation.mutate(
                    {
                      id: statusConfirmation.id,
                      status: statusConfirmation.status,
                    },
                    {
                      onSettled: () => setStatusConfirmation(null),
                    },
                  );
                }
              }}
              className={
                statusConfirmation?.status === "completed"
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : statusConfirmation?.status === "cancelled"
                    ? "bg-rose-600 hover:bg-rose-700 text-white"
                    : ""
              }
            >
              {updateStatusMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                `Confirm & Set as ${statusConfirmation?.status ? statusConfirmation.status.charAt(0).toUpperCase() + statusConfirmation.status.slice(1) : "Status"}`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Details Dialog */}
      <Dialog open={isModalVisible} onOpenChange={handleCloseModal}>
        <DialogContent
          onInteractOutside={(e) => e.preventDefault()}
          className="max-w-4xl max-h-[90vh] flex flex-col p-0"
        >
          <DialogHeader className="px-6 pt-6">
            <div className="flex items-center justify-between pr-6">
              <div>
                <DialogTitle>GRN Details</DialogTitle>
                <DialogDescription>
                  Full details of the Goods Received Note.
                </DialogDescription>
              </div>
              {singleGrnLoading && (
                <div className="text-xs text-muted-foreground animate-pulse">
                  Loading full details...
                </div>
              )}
            </div>
          </DialogHeader>

          <ScrollArea className="flex-1 px-6">
            {modalGrn ? (
              <div className="space-y-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm font-semibold">
                        General Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">PO Trk #:</span>
                        <span className="font-medium">
                          {modalGrn.purchase_order?.tracking_number ||
                            modalGrn.purchaseOrder?.tracking_number ||
                            "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Supplier:</span>
                        <span className="font-medium">
                          {modalGrn.purchase_order?.supplier?.name ||
                            modalGrn.purchaseOrder?.supplier?.name ||
                            "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Store:</span>
                        <span className="font-medium">
                          {modalGrn.store?.name || "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Received Date:
                        </span>
                        <span className="font-medium">
                          {modalGrn.received_date
                            ? formatDate(modalGrn.received_date)
                            : "-"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Received By:
                        </span>
                        <span className="font-medium text-primary">
                          {modalGrn.receivedBy?.full_name ||
                            modalGrn.receivedBy?.name ||
                            modalGrn.received_by?.full_name ||
                            modalGrn.received_by?.name ||
                            modalGrn.receiver?.full_name ||
                            modalGrn.receiver?.name ||
                            getEmployeeName(modalGrn.received_by) ||
                            "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Created By:
                        </span>
                        <span className="font-medium">
                          {modalGrn.createdBy?.full_name ||
                            modalGrn.createdBy?.name ||
                            modalGrn.created_by?.name ||
                            modalGrn.created_by?.full_name ||
                            "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Reference:
                        </span>
                        <span className="font-medium">
                          {modalGrn.reference || "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Payment Method:
                        </span>
                        <span className="font-medium">
                          {modalGrn.paymentMethod?.name ||
                            modalGrn.payment_method?.name ||
                            (typeof modalGrn.payment_method === "string"
                              ? modalGrn.payment_method
                              : "N/A")}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pt-1 border-t">
                        <span className="text-muted-foreground">Status:</span>
                        <div className="flex items-center gap-2">
                          {(() => {
                            const variants = {
                              completed: "success",
                              cancelled: "destructive",
                              pending: "warning",
                            };
                            return (
                              <Badge
                                variant={
                                  variants[modalGrn.status?.toLowerCase()] ||
                                  "default"
                                }
                                className="capitalize font-medium"
                              >
                                {modalGrn.status?.replace(/_/g, " ")}
                              </Badge>
                            );
                          })()}
                          {/* Completed GRNs cannot have status changed */}
                          {hasPermission("goods received note update_status") &&
                            modalGrn.status?.toLowerCase() !== "completed" && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-xs"
                                    disabled={updateStatusMutation.isPending}
                                  >
                                    {updateStatusMutation.isPending &&
                                      updateStatusMutation.variables?.id ===
                                      modalGrn.id ? (
                                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                    ) : null}
                                    Change
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                                    Update Status
                                  </DropdownMenuLabel>
                                  {modalGrn.status?.toLowerCase() !==
                                    "pending" && (
                                      <DropdownMenuItem
                                        onClick={() =>
                                          setStatusConfirmation({
                                            id: modalGrn.id,
                                            status: "pending",
                                            reference: modalGrn.reference || "",
                                          })
                                        }
                                      >
                                        <Clock className="mr-2 h-4 w-4 text-amber-500" />
                                        Mark as Pending
                                      </DropdownMenuItem>
                                    )}
                                  {modalGrn.status?.toLowerCase() !==
                                    "completed" && (
                                      <DropdownMenuItem
                                        onClick={() =>
                                          setStatusConfirmation({
                                            id: modalGrn.id,
                                            status: "completed",
                                            reference: modalGrn.reference || "",
                                          })
                                        }
                                      >
                                        <CheckCircle2 className="mr-2 h-4 w-4 text-emerald-500" />
                                        Mark as Completed
                                      </DropdownMenuItem>
                                    )}
                                  {modalGrn.status?.toLowerCase() !==
                                    "cancelled" && (
                                      <DropdownMenuItem
                                        onClick={() =>
                                          setStatusConfirmation({
                                            id: modalGrn.id,
                                            status: "cancelled",
                                            reference: modalGrn.reference || "",
                                          })
                                        }
                                      >
                                        <XCircle className="mr-2 h-4 w-4 text-rose-500" />
                                        Mark as Cancelled
                                      </DropdownMenuItem>
                                    )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm font-semibold">
                        Financial Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Sub Total:
                        </span>
                        <span className="font-medium">
                          {currencySymbol}
                          {parseFloat(
                            modalGrn.sub_total_amount || 0,
                          ).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Tax Amount:
                        </span>
                        <span className="font-medium">
                          {currencySymbol}
                          {parseFloat(modalGrn.total_tax || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t mt-2">
                        <span className="font-bold text-base">Total:</span>
                        <span className="font-bold text-lg text-primary">
                          {currencySymbol}
                          {parseFloat(modalGrn.total_amount || 0).toFixed(2)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Items Table */}
                {hasPermission("goods received note item read") && (
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm font-semibold">
                        Received Items
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="rounded-md border-x-0 border-b-0 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50">
                            <tr className="border-b">
                              <th className="py-2 px-3 text-left font-medium">
                                Product Details
                              </th>
                              <th className="py-2 px-3 text-center font-medium">
                                Order Qty
                              </th>
                              <th className="py-2 px-3 text-center font-medium">
                                Received
                              </th>
                              <th className="py-2 px-3 text-right font-medium">
                                Unit Cost
                              </th>
                              <th className="py-2 px-3 text-center font-medium">
                                Discount
                              </th>
                              <th className="py-2 px-3 text-right font-medium">
                                Total
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {modalGrn.items &&
                              modalGrn.items.length > 0 ? (
                              modalGrn.items.map((item, idx) => {
                                const product = item.product;
                                const batch = item.batch || item.inventory_batch;
                                const serials =
                                  Array.isArray(item.serial_numbers) &&
                                    item.serial_numbers.length > 0
                                    ? item.serial_numbers
                                    : Array.isArray(batch?.serials)
                                      ? batch.serials
                                        .map((s) =>
                                          typeof s === "object"
                                            ? s.serial_number || s.serial
                                            : s,
                                        )
                                        .filter(Boolean)
                                      : [];

                                return (
                                  <tr key={idx}>
                                    <td className="py-2.5 px-3 max-w-[280px]">
                                      <div className="flex flex-col gap-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="font-medium text-foreground">
                                            {product?.name ||
                                              product?.product_name ||
                                              "N/A"}
                                          </span>
                                          {product?.sku && (
                                            <span className="text-[11px] text-muted-foreground font-mono">
                                              ({product.sku})
                                            </span>
                                          )}
                                          {item.is_foc && (
                                            <Badge
                                              variant="outline"
                                              className="text-[9px] bg-green-50 text-green-700 border-green-200 h-4 px-1"
                                            >
                                              FOC
                                            </Badge>
                                          )}
                                        </div>

                                        {/* Product Relationships: Brand, Category, Unit, Warehouse */}
                                        <div className="flex flex-wrap items-center gap-1 mt-0.5">
                                          {product?.brand?.name && (
                                            <Badge
                                              variant="secondary"
                                              className="text-[10px] px-1.5 py-0 h-4 font-normal"
                                            >
                                              Brand: {product.brand.name}
                                            </Badge>
                                          )}
                                          {(product?.category?.name || product?.categories?.[0]?.name) && (
                                            <Badge
                                              variant="secondary"
                                              className="text-[10px] px-1.5 py-0 h-4 font-normal"
                                            >
                                              {product.category?.name || product.categories[0].name}
                                            </Badge>
                                          )}
                                          {product?.unit?.short_name && (
                                            <Badge
                                              variant="outline"
                                              className="text-[10px] px-1.5 py-0 h-4 font-normal"
                                            >
                                              Unit: {product.unit.short_name}
                                            </Badge>
                                          )}
                                          {product?.warehouse?.name && (
                                            <Badge
                                              variant="outline"
                                              className="text-[10px] px-1.5 py-0 h-4 font-normal"
                                            >
                                              Wh: {product.warehouse.name}
                                              {product.warehouse?.location?.name
                                                ? ` (${product.warehouse.location.name})`
                                                : ""}
                                            </Badge>
                                          )}
                                          {product?.supplier?.name && (
                                            <Badge
                                              variant="outline"
                                              className="text-[10px] px-1.5 py-0 h-4 font-normal text-muted-foreground"
                                            >
                                              Supplier: {product.supplier.name}
                                            </Badge>
                                          )}
                                        </div>

                                        {/* Batch info */}
                                        {batch && (
                                          <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-3 mt-0.5">
                                            {batch.batch_number && (
                                              <span>
                                                Batch:{" "}
                                                <span className="font-mono">
                                                  {batch.batch_number}
                                                </span>
                                              </span>
                                            )}
                                            {batch.expiry_date && (
                                              <span>
                                                Exp:{" "}
                                                {formatDate(batch.expiry_date)}
                                              </span>
                                            )}
                                          </div>
                                        )}

                                        {/* Serials */}
                                        {serials.length > 0 && (
                                          <div className="flex flex-wrap gap-1 mt-1 items-center">
                                            <span className="text-[10px] font-semibold text-muted-foreground">
                                              Serials ({serials.length}):
                                            </span>
                                            {serials
                                              .slice(0, 6)
                                              .map((sn, sIdx) => (
                                                <Badge
                                                  key={sIdx}
                                                  variant="secondary"
                                                  className="text-[9px] px-1.5 py-0 h-4 font-mono font-normal"
                                                >
                                                  {sn}
                                                </Badge>
                                              ))}
                                            {serials.length > 6 && (
                                              <Badge
                                                variant="outline"
                                                className="text-[9px] px-1 py-0 h-4 text-muted-foreground"
                                              >
                                                +{serials.length - 6} more
                                              </Badge>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                    <td className="py-2.5 px-3 text-center">
                                      {item.order_quantity ?? "-"}
                                    </td>
                                    <td className="py-2.5 px-3 text-center font-semibold text-primary">
                                      {item.received_quantity ?? 0}
                                    </td>
                                    <td className="py-2.5 px-3 text-right">
                                      {currencySymbol}
                                      {parseFloat(item.unit_cost || 0).toFixed(
                                        2,
                                      )}
                                    </td>
                                    <td className="py-2.5 px-3 text-center text-red-500">
                                      {item.is_foc ? (
                                        <span className="text-[10px] bg-green-50 text-green-600 px-1 py-0.5 rounded font-medium">
                                          -100%
                                        </span>
                                      ) : parseFloat(item.discount_amount || 0) >
                                        0 ? (
                                        <span className="text-xs font-medium">
                                          -{currencySymbol}
                                          {parseFloat(
                                            item.discount_amount,
                                          ).toFixed(2)}
                                        </span>
                                      ) : (
                                        "-"
                                      )}
                                    </td>
                                    <td className="py-2.5 px-3 text-right font-semibold">
                                      {currencySymbol}
                                      {parseFloat(item.total_cost || 0).toFixed(
                                        2,
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td
                                  colSpan="6"
                                  className="py-6 text-center text-muted-foreground"
                                >
                                  No items found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Notes & Document */}
                {(modalGrn.notes || modalGrn.document) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {modalGrn.notes && (
                      <Card>
                        <CardHeader className="py-3">
                          <CardTitle className="text-sm font-semibold">
                            Notes
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-xs text-muted-foreground whitespace-pre-wrap">
                            {modalGrn.notes}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    {modalGrn.document && (
                      <Card>
                        <CardHeader className="py-3">
                          <CardTitle className="text-sm font-semibold">
                            Attached Document
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="text-xs">
                            <p className="font-medium">
                              {modalGrn.document}
                            </p>
                            <p className="text-muted-foreground capitalize">
                              Type: {modalGrn.document_type}
                            </p>
                          </div>
                          {modalGrn.document_url && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full h-8 text-xs"
                              onClick={() =>
                                window.open(modalGrn.document_url, "_blank")
                              }
                            >
                              View Document
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
              </div>
            ) : singleGrnLoading ? (
              <div className="flex items-center justify-center py-16">
                <p className="text-muted-foreground text-sm">Loading GRN details...</p>
              </div>
            ) : null}
          </ScrollArea>
          <DialogFooter className="px-6 py-6 border-t mt-4">
            <Button
              onClick={() => handleCloseModal(false)}
              variant="secondary"
            >
              Close
            </Button>
            {modalGrn && hasPermission("report goods received note view") && (
              <Button
                onClick={() => {
                  handleCloseModal(false);
                  handleGeneratePdf(modalGrn.id);
                }}
              >
                <FileText className="mr-2 h-4 w-4" /> Download PDF
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Ready Dialog */}
      <Dialog open={showPdfModal} onOpenChange={handleClosePdfModal}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>PDF Generated Successfully</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center p-4 space-y-4">
            <div className="text-green-600">
              <FileText size={48} />
            </div>
            <p className="text-center text-muted-foreground">
              Your GRN PDF is ready to download or print.
            </p>
            <div className="flex gap-4">
              <Button onClick={handleDownloadPDF}>
                <Download className="mr-2 h-4 w-4" /> Download
              </Button>
              <Button variant="secondary" onClick={handlePrintPDF}>
                <Printer className="mr-2 h-4 w-4" /> Print
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default GRNList;
